// src/contexts/ModuleContext.jsx - Updated to read from 'clients' collection
import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { db } from '../utils/firebase';

const ModuleContext = createContext();

export function useModules() {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModules must be used within a ModuleProvider');
  }
  return context;
}
//test
// Default module configuration
const DEFAULT_MODULE_CONFIG = {
  home: {
    id: 'home',
    name: 'Core',
    description: 'Core salon configuration and content management',
    enabled: true,
    tabs: [] // No tabs - just shows the salon image page
  },
  core: {
    id: 'core',
    name: 'Core',
    description: 'Core salon configuration and content management',
    enabled: true,
    tabs: [
      'branding',
      'typography',
      'images',
      'aboutUs',
      'contact',
      'hours',
      'services',
      'products',
      'staff',
      'customers',
      'wallet-topups',
      'file-integration',
      'customCSS',
      'core-ai-agent'
    ]
  },
  promotions: {
    id: 'promotions',
    name: 'Promotions',
    description: 'Create and manage promotional campaigns and offers',
    enabled: true,
    tabs: ['loyalty-program', 'promotions', 'loyalty-ai-agent']
  },
  communications: {
    id: 'communications',
    name: 'Communications Management', 
    description: 'Email, push notification and WhatsApp campaigns',
    enabled: true,
    tabs: [
      'email-campaigns',
      'push-campaigns',
      'whatsapp-campaigns',
      'social-media-campaigns',
      'portal-personalisation',
      'campaigns-listing',
      'communications-ai-agent'
    ]
  },
  bookings: {
    id: 'bookings',
    name: 'Bookings Management',
    description: 'Calendar management and booking oversight',
    enabled: true,
    tabs: ['booking-calendar', 'booking-requests', 'leave-management', 'working-hours', 'bookings-ai-agent']
  },
  reports: {
    id: 'reports',
    name: 'Reports Management',
    description: 'Analytics, reporting, and business intelligence',
    enabled: true,
    tabs: [
      'query-builder',
      'analytics-dashboard',
      'targets',
      'staff-payroll',
      'reports-ai-agent'
    ]
  },
  bespokeai: {
    id: 'bespokeai',
    name: 'Bespoke AI',
    description: 'AI-powered recommendations and automation for salon management',
    enabled: true,
    tabs: [
      'loyalty-ai',
      'communications-ai'
    ]
  }
};

// The AI agent pages ("Bookings AI" under Bookings Management, "Loyalty AI" under Loyalty Management, "Communications AI"
// under Communications Management) are part of Bespoke AI: each is only a real tab when Bespoke AI and its matching
// sub-module are enabled (the admin nav still shows it, dimmed, otherwise).
const AGENT_TABS = [
  { moduleId: 'bookings', tab: 'bookings-ai-agent', flag: 'bookingsAI' },
  { moduleId: 'promotions', tab: 'loyalty-ai-agent', flag: 'loyaltyAI' },
  { moduleId: 'communications', tab: 'communications-ai-agent', flag: 'communicationsAI' },
  { moduleId: 'reports', tab: 'reports-ai-agent', flag: 'reportsAI' },
  { moduleId: 'core', tab: 'core-ai-agent', flag: null },
];
function applyBookingsAgentTab(config, enabledModules) {
  AGENT_TABS.forEach(({ moduleId, tab, flag }) => {
    // flag=null means the tab only requires bespokeai to be enabled (no sub-module flag needed)
    const allowed = config.bespokeai.enabled && (flag === null || enabledModules[flag] === true);
    config[moduleId] = {
      ...config[moduleId],
      tabs: DEFAULT_MODULE_CONFIG[moduleId].tabs.filter(t => t !== tab || allowed)
    };
  });
}

export function ModuleProvider({ children }) {
  const [moduleConfig, setModuleConfig] = useState(DEFAULT_MODULE_CONFIG);
  const [activeModule, setActiveModule] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentSalonId, setCurrentSalonId] = useState(null);

  // Load module configuration from 'clients' collection (PRIMARY SOURCE OF TRUTH)
  const loadModuleConfig = async (salonId) => {
    try {
      setLoading(true);
      setCurrentSalonId(salonId);

      console.log('═══════════════════════════════════════════════════════');
      console.log('🔧 ModuleContext: Loading module config');
      console.log('═══════════════════════════════════════════════════════');
      console.log('📍 Salon ID:', salonId);

      // PRIMARY: Get the client configuration from 'clients' collection
      // Try multiple possible client document IDs
      console.log('🔍 Step 1: Attempting to load from clients collection...');

      let clientDoc;
      let clientDocId;

      // Strategy 1: Try exact salonId match (e.g., "demo-nail")
      console.log(`   Trying: clients/${salonId}`);
      clientDoc = await getDoc(doc(db, 'clients', salonId));

      if (!clientDoc.exists()) {
        // Strategy 2: Try salonId + "-client" (e.g., "demo-nail-client")
        const clientIdWithSuffix = `${salonId}-client`;
        console.log(`   Not found, trying: clients/${clientIdWithSuffix}`);
        clientDoc = await getDoc(doc(db, 'clients', clientIdWithSuffix));
        clientDocId = clientIdWithSuffix;
      } else {
        clientDocId = salonId;
      }

      if (clientDoc.exists()) {
        console.log(`   ✅ Found at: clients/${clientDocId}`);
        const clientData = clientDoc.data();
        const enabledModules = clientData.enabledModules || {};

        console.log('📋 Client Name:', clientData.name || 'N/A');
        console.log('📦 Enabled Modules from Firestore:', JSON.stringify(enabledModules, null, 2));

        // Update module configuration based on clients collection settings
        const updatedConfig = { ...DEFAULT_MODULE_CONFIG };

        console.log('🔄 Step 2: Applying module configuration...');

        // Set enabled status based on clients collection configuration
        Object.keys(updatedConfig).forEach(moduleId => {
          const previousState = updatedConfig[moduleId].enabled;

          if (moduleId !== 'home' && moduleId !== 'core') { // Home and Core modules are always enabled
            const firestoreValue = enabledModules[moduleId];
            const isEnabled = firestoreValue === true;
            updatedConfig[moduleId].enabled = isEnabled;

            console.log(`   ${moduleId}:`, {
              firestoreValue,
              firestoreValueType: typeof firestoreValue,
              isStrictlyTrue: firestoreValue === true,
              previousState,
              newState: updatedConfig[moduleId].enabled
            });
          } else {
            console.log(`   ${moduleId}: Always enabled (core module)`);
          }
        });

        // Filter BespokeAI tabs based on individual AI sub-module flags
        if (updatedConfig.bespokeai.enabled) {
          const AI_TAB_MAP = {
            'communications-ai': enabledModules.communicationsAI === true,
            'loyalty-ai': enabledModules.loyaltyAI === true
          };
          updatedConfig.bespokeai = {
            ...updatedConfig.bespokeai,
            tabs: DEFAULT_MODULE_CONFIG.bespokeai.tabs.filter(tab => AI_TAB_MAP[tab])
          };
          console.log('🤖 BespokeAI tabs after sub-module filtering:', updatedConfig.bespokeai.tabs);
        }

        console.log('✅ Final module configuration:',
          Object.keys(updatedConfig).map(key => `${key}=${updatedConfig[key].enabled}`).join(', '));
        console.log('═══════════════════════════════════════════════════════\n');

        applyBookingsAgentTab(updatedConfig, enabledModules);
        setModuleConfig(updatedConfig);
      } else {
        console.warn('⚠️  WARNING: No client document found by direct ID lookup');
        console.log('📍 Attempted paths:');
        console.log(`   - clients/${salonId}`);
        console.log(`   - clients/${salonId}-client`);

        // Strategy 3: Query clients collection by salonId field (handles document ID ≠ salonId)
        console.log('🔍 Step 2: Querying clients collection by salonId field...');
        const clientsQuery = query(collection(db, 'clients'), where('salonId', '==', salonId));
        const clientsSnapshot = await getDocs(clientsQuery);

        if (!clientsSnapshot.empty) {
          const clientData = clientsSnapshot.docs[0].data();
          const foundDocId = clientsSnapshot.docs[0].id;
          const enabledModules = clientData.enabledModules || {};

          console.log(`   ✅ Found via salonId query at: clients/${foundDocId}`);
          console.log('📦 Enabled Modules from Firestore:', JSON.stringify(enabledModules, null, 2));

          const updatedConfig = { ...DEFAULT_MODULE_CONFIG };

          Object.keys(updatedConfig).forEach(moduleId => {
            if (moduleId !== 'home' && moduleId !== 'core') {
              const firestoreValue = enabledModules[moduleId];
              updatedConfig[moduleId].enabled = firestoreValue === true;
              console.log(`   ${moduleId}: ${firestoreValue} → ${updatedConfig[moduleId].enabled}`);
            }
          });

          if (updatedConfig.bespokeai.enabled) {
            const AI_TAB_MAP = {
              'communications-ai': enabledModules.communicationsAI === true,
              'loyalty-ai': enabledModules.loyaltyAI === true
            };
            updatedConfig.bespokeai = {
              ...updatedConfig.bespokeai,
              tabs: DEFAULT_MODULE_CONFIG.bespokeai.tabs.filter(tab => AI_TAB_MAP[tab])
            };
          }

          console.log('✅ Final module configuration (salonId query):',
            Object.keys(updatedConfig).map(key => `${key}=${updatedConfig[key].enabled}`).join(', '));
          console.log('═══════════════════════════════════════════════════════\n');

          applyBookingsAgentTab(updatedConfig, enabledModules);
          setModuleConfig(updatedConfig);
          return; // resolved — skip salons fallback
        }

        // FALLBACK: Try salons collection (legacy support)
        // NOTE: The salons collection is publicly readable (allow read: if true), so this
        // path is taken by customer portal users who cannot access the clients collection.
        // We copy enabledModules to salons docs so customers see correct module visibility.
        console.log('🔄 Step 3 (FALLBACK): Trying salons collection...');
        const salonDoc = await getDoc(doc(db, 'salons', salonId));

        if (salonDoc.exists()) {
          const salonData = salonDoc.data();
          const enabledModules = salonData.enabledModules || salonData.EnabledModules;

          console.log('✅ SUCCESS: Salon document found (fallback)');
          console.log('📦 Enabled Modules:', JSON.stringify(enabledModules ?? '(not set)', null, 2));

          // If the salons doc has no enabledModules field (e.g. a manually-created salon
          // whose module config lives only in the clients collection), fall back to the
          // default configuration so ALL modules are available.  This prevents the customer
          // portal from ghosting tabs just because the salons doc was never populated with
          // an explicit enabledModules map.
          if (!enabledModules || Object.keys(enabledModules).length === 0) {
            console.log('⚠️  salons doc has no enabledModules — using default (all enabled)');
            console.log('═══════════════════════════════════════════════════════\n');
            setModuleConfig(DEFAULT_MODULE_CONFIG);
          } else {
          const updatedConfig = { ...DEFAULT_MODULE_CONFIG };

          Object.keys(updatedConfig).forEach(moduleId => {
            if (moduleId !== 'home' && moduleId !== 'core') {
              const firestoreValue = enabledModules[moduleId];
              updatedConfig[moduleId].enabled = firestoreValue === true;
              console.log(`   ${moduleId}: ${firestoreValue} (type: ${typeof firestoreValue}) → ${updatedConfig[moduleId].enabled}`);
            }
          });

          if (updatedConfig.bespokeai.enabled) {
            const AI_TAB_MAP = {
              'communications-ai': enabledModules.communicationsAI === true,
              'loyalty-ai': enabledModules.loyaltyAI === true
            };
            updatedConfig.bespokeai = {
              ...updatedConfig.bespokeai,
              tabs: DEFAULT_MODULE_CONFIG.bespokeai.tabs.filter(tab => AI_TAB_MAP[tab])
            };
          }

          console.log('✅ Final module configuration (fallback):',
            Object.keys(updatedConfig).map(key => `${key}=${updatedConfig[key].enabled}`).join(', '));
          console.log('═══════════════════════════════════════════════════════\n');

          applyBookingsAgentTab(updatedConfig, enabledModules);
          setModuleConfig(updatedConfig);
          }
        } else {
          console.error('❌ ERROR: No configuration found in either clients or salons collection');
          console.log('📍 Attempted paths:');
          console.log(`   - clients/${salonId}`);
          console.log(`   - clients/${salonId}-client`);
          console.log(`   - salons/${salonId}`);
          console.log('⚠️  Using default configuration (all modules enabled)');
          console.log('═══════════════════════════════════════════════════════\n');

          // Use default configuration if no documents found
          setModuleConfig(DEFAULT_MODULE_CONFIG);
        }
      }
    } catch (error) {
      console.error('═══════════════════════════════════════════════════════');
      console.error('❌ CRITICAL ERROR loading module configuration');
      console.error('═══════════════════════════════════════════════════════');
      console.error('Salon ID:', salonId);
      console.error('Error:', error);
      console.error('⚠️  Falling back to default configuration');
      console.error('═══════════════════════════════════════════════════════\n');

      // Fall back to default configuration on error
      setModuleConfig(DEFAULT_MODULE_CONFIG);
    } finally {
      setLoading(false);
    }
  };

  // Get enabled modules
  const getEnabledModules = () => {
    return Object.values(moduleConfig).filter(module => module.enabled);
  };

  // Get all modules (including disabled ones)
  const getAllModules = () => {
    return Object.values(moduleConfig);
  };

  // Check if a specific module is enabled
  const isModuleEnabled = (moduleId) => {
    return moduleConfig[moduleId]?.enabled || false;
  };

  // Check if a specific tab is available in any enabled module
  const isTabAvailable = (tabId) => {
    const enabledModules = getEnabledModules();
    return enabledModules.some(module => module.tabs.includes(tabId));
  };

  // Get the module that contains a specific tab
  const getModuleForTab = (tabId) => {
    return Object.values(moduleConfig).find(module => 
      module.enabled && module.tabs.includes(tabId)
    );
  };

  // Switch to a specific module and optionally a tab within that module
  const switchToModule = (moduleId, tabId = null) => {
    // Handle special case for module overview
    if (moduleId === null) {
      setActiveModule(null);
      setActiveTab(null);
      return true;
    }

    if (!isModuleEnabled(moduleId)) {
      console.warn(`Module ${moduleId} is not enabled for salon:`, currentSalonId);
      return false; // Return false to indicate module access denied
    }

    setActiveModule(moduleId);
    
    if (tabId && moduleConfig[moduleId].tabs.includes(tabId)) {
      setActiveTab(tabId);
    } else {
      // Set to first available tab in the module
      setActiveTab(moduleConfig[moduleId].tabs[0]);
    }
    return true; // Return true to indicate success
  };

  // Switch to a specific tab
  const switchToTab = (tabId) => {
    const module = getModuleForTab(tabId);
    if (module) {
      setActiveModule(module.id);
      setActiveTab(tabId);
      return true;
    } else {
      console.warn(`Tab ${tabId} is not available in any enabled module for salon:`, currentSalonId);
      return false;
    }
  };

  // Get tabs for the currently active module
  const getActiveModuleTabs = () => {
    const module = moduleConfig[activeModule];
    return module ? module.tabs : [];
  };

  // Update module configuration
  const updateModuleConfig = (newConfig) => {
    console.log('Updating module configuration for salon:', currentSalonId);
    setModuleConfig(newConfig);
  };

  // Get available modules list
  const getAvailableModules = () => {
    return Object.keys(moduleConfig).map(key => ({
      id: key,
      ...moduleConfig[key]
    }));
  };

  // Initialize module config for a salon
  const initializeForSalon = async (salonId) => {
    if (currentSalonId !== salonId) {
      await loadModuleConfig(salonId);
    }
  };

  const value = {
    moduleConfig,
    activeModule,
    activeTab,
    loading,
    currentSalonId,
    
    // Functions
    loadModuleConfig,
    getEnabledModules,
    getAllModules,
    isModuleEnabled,
    isTabAvailable,
    getModuleForTab,
    switchToModule,
    switchToTab,
    getActiveModuleTabs,
    updateModuleConfig,
    getAvailableModules,
    initializeForSalon,
    
    // Direct setters for backward compatibility
    setActiveModule,
    setActiveTab
  };

  return (
    <ModuleContext.Provider value={value}>
      {children}
    </ModuleContext.Provider>
  );
}